import type { SkierCounts } from '@/store/book-ski-pass.store';

export function isValidBirthDate(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return false;
  }

  const [yearPart, monthPart, dayPart] = trimmed.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false;
  }

  const currentYear = new Date().getUTCFullYear();
  if (year < 1900 || year > currentYear) {
    return false;
  }

  const date = new Date(`${trimmed}T00:00:00.000Z`);
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  );
}

export function isValidEmail(value: string): boolean {
  // common server-side RFC-style constraints
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.length > 254) return false;
  if (/\s/.test(trimmed)) return false;

  const atIndex = trimmed.indexOf('@');
  if (atIndex <= 0 || atIndex !== trimmed.lastIndexOf('@') || atIndex === trimmed.length - 1) {
    return false;
  }

  const localPart = trimmed.slice(0, atIndex);
  const domainPart = trimmed.slice(atIndex + 1);

  if (localPart.length > 64) return false;
  if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) return false;
  if (!/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(localPart)) return false;

  if (!domainPart.includes('.') || domainPart.includes('..')) return false;
  if (domainPart.length > 253) return false;

  const labels = domainPart.split('.');
  if (labels.some((label) => label.length === 0 || label.length > 63)) return false;
  if (labels.some((label) => !/^[A-Za-z0-9-]+$/.test(label))) return false;
  if (labels.some((label) => label.startsWith('-') || label.endsWith('-'))) return false;

  const tld = labels.at(-1);
  if (!tld) return false;
  if (!/^[A-Za-z]{2,63}$/.test(tld)) return false;

  return true;
}

export function isValidPhone(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (!/^\+?[\d\s()-]+$/.test(trimmed)) return false;

  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

export function totalPeople(skierCounts: SkierCounts): number {
  return Object.values(skierCounts).reduce((sum, count) => sum + count, 0);
}

export function peopleByAgeCategoryLabel(skierCounts: SkierCounts): string {
  const labels: string[] = [];
  for (const [ageCategory, count] of Object.entries(skierCounts)) {
    let label = ageCategory;
    if (count === 0) continue;
    if (count > 1) {
      if (ageCategory.toLowerCase() === 'child') {
        label = 'Children';
      } else {
        label = `${ageCategory}s`;
      }
    }
    labels.push(`${count} ${label}`);
  }
  // it should be X, Y and Z, not X, Y, Z
  if (labels.length > 1) {
    const lastLabel = labels.pop();
    if (lastLabel) {
      return `${labels.join(', ')} and ${lastLabel}`;
    }
  }
  return labels.join(', ');
}